/**
 * Tutorial Step Definitions
 * 
 * Defines tutorial steps for main page and category page workflows.
 * Each step targets specific UI elements and provides contextual explanations.
 * 
 * Requirements: 1.2, 1.3, 2.1, 2.2, 2.3, 2.5, 3.1, 3.2, 3.4
 */

import type { ResponsiveTutorialStep } from '../utils/responsiveTutorial';

/**
 * Main Page Tutorial Steps
 * 
 * Guides users through the textile classification workflow:
 * - Camera interface and image capture
 * - Image slots and management
 * - Classification submission and results
 * - History viewing
 * 
 * Now includes responsive configurations for different screen sizes.
 * 
 * Requirements: 1.2, 2.1, 2.2, 2.3, 2.4, 2.5
 */
export const mainPageSteps: ResponsiveTutorialStep[] = [
  {
    id: 'welcome',
    target: '.nav-logo',
    content: (
      <div>
        <p>SIFT is the future of textile sorting - capture textile images and get AI-powered classification results.</p>
      </div>
    ),
    title: 'Welcome to SIFT',
    placement: 'bottom',
    section: 'main',
    required: true,
    disableBeacon: true,
    hideCloseButton: false,
  },

  {
    id: 'camera-view',
    target: '.camera-video-fullscreen',
    content: (
      <div>
        <p>Position your textile in the camera view with good lighting for best results.</p>
      </div>
    ),
    title: 'Live Camera',
    placement: 'bottom',
    section: 'main',
    required: true,
  },

  {
    id: 'camera-switch',
    target: '.camera-switch-fab',
    content: (
      <div>
        <p>Switch between front and back cameras if available.</p>
      </div>
    ),
    title: 'Switch Camera',
    placement: 'left',
    section: 'main',
    required: false,
    hideCloseButton: true,
  },

  {
    id: 'image-slots',
    target: '.captured-images-overlay',
    content: (
      <div>
        <p>Capture up to 3 images that show all sides for the best results</p>
      </div>
    ),
    title: 'Image Slots',
    placement: 'left',
    section: 'main',
    required: true,
    responsivePlacement: {
      mobile: 'bottom',
      tablet: 'left',
    },
  },

  {
    id: 'capture-button',
    target: '.capture-button',
    content: (
      <div>
        <p>Click here to capture.</p>
      </div>
    ),
    title: 'Capture Button',
    placement: 'top',
    section: 'main',
    required: true,
  },

  {
    id: 'submit-button',
    target: '.submit-button',
    content: (
      <div>
        <p>Click Submit to send your images for AI analysis when you're ready.</p>
      </div>
    ),
    title: 'Submit for Analysis',
    placement: 'top',
    section: 'main',
    required: true,
  },

  {
    id: 'progress-indicator',
    target: '.progress-overlay',
    content: (
      <div>
        <p>Watch the progress as AI analyzes your images.</p>
      </div>
    ),
    title: 'Processing',
    placement: 'center',
    section: 'main',
    required: false,
    hideCloseButton: true,
  },

  {
    id: 'results-overlay',
    target: '.classification-result-overlay',
    content: (
      <div>
        <p>View detailed classification results including textile type, material, and care instructions.</p>
      </div>
    ),
    title: 'Results',
    placement: 'center',
    section: 'main',
    required: true,
    hideCloseButton: true,
  },

  {
    id: 'history-thumbnails',
    target: '.history-thumbnails',
    content: (
      <div>
        <p>Access your recent classifications here (Business+ feature).</p>
      </div>
    ),
    title: 'Recent Items',
    placement: 'left',
    section: 'main',
    required: false,
    hideCloseButton: true,
  },



  // Credits step - Mobile version (shown on mobile/tablet when .nav-credits-mobile-header is visible)
  {
    id: 'credits-counter-mobile',
    target: '.nav-credits-mobile-header',
    content: (
      <div>
        <p>Each submission costs credits which covers the cost to run our AI system. You can see your remaining credits here.</p>
      </div>
    ),
    title: 'Credits',
    placement: 'bottom',
    section: 'main',
    required: false, // Optional - will be skipped if element not visible
    disableBeacon: false,
    hideCloseButton: false,
  },

  // Credits step - Desktop version (shown on desktop when .nav-credits-desktop is visible)
  {
    id: 'credits-counter-desktop',
    target: '.nav-credits-desktop',
    content: (
      <div>
        <p>Each submission costs credits which covers the cost to run our AI system. You can see your remaining credits here.</p>
      </div>
    ),
    title: 'Credits',
    placement: 'bottom',
    section: 'main',
    required: false, // Optional - will be skipped if element not visible
    disableBeacon: false,
    hideCloseButton: false,
  },

  {
    id: 'tutorial-complete',
    target: '.nav-logo',
    content: (
      <div>
        <p>You're ready to start classifying textiles. Check out the Category page tutorial next or restart this tutorial anytime from the Account page.</p>
      </div>
    ),
    title: 'Ready to Start!',
    placement: 'bottom',
    section: 'main',
    required: true,
    disableBeacon: true,
    hideCloseButton: false,
  },
];

/**
 * Category Page Tutorial Steps
 * 
 * Guides users through category management and pricing configuration.
 * Now includes responsive configurations for different screen sizes.
 * 
 * Requirements: 1.3, 3.1, 3.2, 3.3, 3.4, 3.5
 */
export const categoryPageSteps: ResponsiveTutorialStep[] = [
  {
    id: 'categories-welcome',
    target: '.tutorial-trigger-btn',
    content: (
      <div>
        <p>Control which textile types the system can identify and classify.</p>
      </div>
    ),
    title: 'Categories Dashboard',
    placement: 'bottom',
    section: 'categories',
    required: true,
    disableBeacon: true,
  },

  {
    id: 'criteria-stats',
    target: '.header-content .criteria-stats', // More specific selector
    content: (
      <div>
        <p>Shows how many classification types are currently enabled.</p>
      </div>
    ),
    title: 'Statistics',
    placement: 'bottom',
    section: 'categories',
    required: true,
  },

  {
    id: 'subscription-banner',
    target: '.dashboard-header .subscription-banner', // More specific selector
    content: (
      <div>
        <p>Your subscription plan determines what features you can access here.</p>
      </div>
    ),
    title: 'Subscription Info',
    placement: 'bottom',
    section: 'categories',
    required: true,
  },

  {
    id: 'criterion-cards',
    target: '.criteria-grid .criterion-card:first-child', // Target first card specifically
    content: (
      <div>
        <p>Each card represents a different classification type - click to see details.</p>
      </div>
    ),
    title: 'Criteria Cards',
    placement: 'bottom',
    section: 'categories',
    required: true,
  },

  {
    id: 'toggle-switch',
    target: '.criteria-grid .criterion-card:first-child .toggle-switch', // Target first card's toggle
    content: (
      <div>
        <p>Use these switches to enable or disable classification types.</p>
      </div>
    ),
    title: 'Toggle Controls',
    placement: 'bottom',
    section: 'categories',
    required: true,
  },

  {
    id: 'category-management',
    target: '.criteria-grid .criterion-card:first-child', // Target first card specifically
    content: (
      <div>
        <p>Click any card to view and manage individual categories within it.</p>
      </div>
    ),
    title: 'Category Management',
    placement: 'bottom',
    section: 'categories',
    required: true,
  },

  {
    id: 'custom-criteria',
    target: '.criteria-grid .add-custom-card', // More specific selector
    content: (
      <div>
        <p>Create custom classification types with a premium subscription.</p>
      </div>
    ),
    title: 'Custom Criteria',
    placement: 'bottom',
    section: 'categories',
    required: false,
    hideCloseButton: true,
  },

  {
    id: 'save-changes',
    target: '.dashboard-footer .save-btn', // More specific selector
    content: (
      <div>
        <p>Click here to save your changes before leaving this page.</p>
      </div>
    ),
    title: 'Save Changes',
    placement: 'top',
    section: 'categories',
    required: true,
  },

  {
    id: 'categories-complete',
    target: '.tutorial-trigger-btn',
    content: (
      <div>
        <p>You're all set to customize your textile classification preferences!</p>
      </div>
    ),
    title: 'Configuration Complete!',
    placement: 'bottom',
    section: 'categories',
    required: true,
    disableBeacon: true,
  },
];

/**
 * Combined tutorial steps for full application walkthrough
 * 
 * Includes both main page and category page steps in logical sequence.
 * Now uses responsive tutorial steps.
 */
export const combinedSteps: ResponsiveTutorialStep[] = [
  ...mainPageSteps.slice(0, -1), // All main page steps except the completion step
  ...categoryPageSteps.slice(1, -1), // Category steps except welcome and completion
  {
    id: 'full-tutorial-complete',
    target: 'body',
    content: (
      <div>
        <p>Tutorial complete - you're ready to capture and classify textiles!</p>
      </div>
    ),
    title: 'Tutorial Complete!',
    placement: 'center',
    section: 'combined',
    required: true,
    disableBeacon: true,
    styles: {
      options: {
        primaryColor: '#4a90e2',
        width: 500,
      },
    },
  },
];

/**
 * Get tutorial steps for a specific section
 * 
 * @param section - The tutorial section ('main', 'categories', or 'combined')
 * @returns Array of tutorial steps for the specified section
 */
export function getTutorialSteps(section: 'main' | 'categories' | 'combined'): ResponsiveTutorialStep[] {
  switch (section) {
    case 'main':
      return mainPageSteps;
    case 'categories':
      return categoryPageSteps;
    case 'combined':
      return combinedSteps;
    default:
      return mainPageSteps;
  }
}

/**
 * Get tutorial steps filtered by requirement
 * 
 * @param required - Whether to include only required steps
 * @returns Array of tutorial steps filtered by requirement status
 */
export function getRequiredSteps(section: 'main' | 'categories' | 'combined', required: boolean = true): ResponsiveTutorialStep[] {
  const steps = getTutorialSteps(section);
  return steps.filter(step => step.required === required);
}

/**
 * Get tutorial step by ID
 * 
 * @param stepId - The unique step identifier
 * @param section - The tutorial section to search in
 * @returns The tutorial step or undefined if not found
 */
export function getTutorialStepById(stepId: string, section: 'main' | 'categories' | 'combined' = 'main'): ResponsiveTutorialStep | undefined {
  const steps = getTutorialSteps(section);
  return steps.find(step => step.id === stepId);
}